/** Mission selector for one device: the most recent missions in a dropdown,
 *  with a last entry that opens every mission Sentio recorded for the device
 *  in a dialog. Picking a mission in either place selects it for the whole
 *  page. Missions are grouped as Sentio groups them (one per intervention_id);
 *  the live one is the intervention that started last. */

import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { MissionListRow } from "../types";

/** Height of the controls in the page's top bar: this dropdown and the
 *  buttons beside it, so they line up. */
export const TOP_CONTROL_HEIGHT = 36;

/** How many missions the dropdown shows before "View all". */
const RECENT = 5;
const VIEW_ALL = "__view_all__";

function when(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function duration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const minutes = Math.max(0, Math.round((Date.parse(end) - Date.parse(start)) / 60000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function StatusChips(props: { row: MissionListRow }) {
  return (
    <>
      {props.row.current && !props.row.ended ? <Chip size="small" color="success" label="live" /> : null}
      {props.row.ended ? <Chip size="small" variant="outlined" label="ended" /> : null}
      {props.row.simulated ? <Chip size="small" color="secondary" variant="outlined" label="simulated" /> : null}
      {!props.row.in_engine ? <Chip size="small" variant="outlined" label="not processed" /> : null}
    </>
  );
}

type Props = {
  missions: MissionListRow[];
  missionId: string | null;
  following: boolean;
  onSelect: (missionId: string) => void;
};

export function MissionPicker(props: Props) {
  const { missions, missionId, onSelect } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // The recent ones, plus the selected mission when it is older than those.
  const options = useMemo(() => {
    const recent = missions.slice(0, RECENT);
    const selected = missions.find((m) => m.mission_id === missionId);
    return selected && !recent.includes(selected) ? [...recent, selected] : recent;
  }, [missions, missionId]);

  const filtered = missions.filter((m) => m.intervention_id.toLowerCase().includes(query.trim().toLowerCase()));

  const choose = (next: string) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <>
      <TextField
        select
        size="small"
        label="Mission"
        value={missionId ?? ""}
        onChange={(event) => (event.target.value === VIEW_ALL ? setOpen(true) : onSelect(event.target.value))}
        sx={{ minWidth: 320, "& .MuiInputBase-root": { height: TOP_CONTROL_HEIGHT } }}
        SelectProps={{
          renderValue: (value) => {
            const row = missions.find((m) => m.mission_id === value);
            if (!row) return "";
            const tags = [row.current && !row.ended ? "live" : null, row.ended ? "ended" : null];
            return [row.intervention_id, ...tags, row.simulated ? "simulated" : null].filter(Boolean).join(" · ");
          },
        }}
      >
        {options.map((row) => (
          <MenuItem key={row.mission_id} value={row.mission_id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">{row.intervention_id}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {when(row.started_at ?? row.updated_at)}
                </Typography>
              </Box>
              <StatusChips row={row} />
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem value={VIEW_ALL}>
          <Typography variant="body2" color="primary">
            View all missions ({missions.length})…
          </Typography>
        </MenuItem>
      </TextField>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>All missions for this device</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One mission per intervention the device reported, as Sentio records them. The live mission is the one that
            started last, until it ends. &quot;Not processed&quot; means Sentio has the data but the Mission Coordinator
            engine never saw it; &quot;simulated&quot; that the mission simulator played it, not a real device.
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter by intervention id"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ mb: 1 }}
          />
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Mission</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Last data</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Engine</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow
                    key={row.mission_id}
                    hover
                    selected={row.mission_id === missionId}
                    onClick={() => choose(row.mission_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {row.intervention_id}
                    </TableCell>
                    <TableCell>{when(row.started_at)}</TableCell>
                    <TableCell>{when(row.last_data_at)}</TableCell>
                    <TableCell>{duration(row.started_at, row.last_data_at)}</TableCell>
                    <TableCell>{row.state_version !== null ? `v${row.state_version}` : "—"}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                        <StatusChips row={row} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No mission matches.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
